import clsx from 'clsx'

// Components
import { Pill, Button, IconCheckmark, Link } from '@tracklib/kit'

// Hooks
import { useProfile } from 'modules/profile/hooks'

// Types
import { SubscriptionTier, TierPeriod } from 'types'

// Utils
import { priceString, priceFormatWithVAT } from 'components/base/price'
import { CURRENCY } from '@constants'

import styles from './offer.css'

const monthsToText = (months: number): string => {
  if (months === 1) {
    return 'month'
  }
  if (months === 12) {
    return 'year'
  }
  if (months === 3) {
    return 'quarter'
  }
  return `${months} months`
}

type OfferChildProps = {
  children: number | string
}

export const OfferPill = ({ children, offer, hasDiscount }: { children: string; offer: string; hasDiscount: boolean }) => (
  <Pill
    size="small"
    className={clsx(
      styles.pill,
      hasDiscount && styles.goldPill,
      offer === 'Standard' && styles.preferred,
      offer === 'Professional' && styles.best,
    )}
  >
    {children}
  </Pill>
)

export const OfferTitle = ({ children }: OfferChildProps): JSX.Element => <p className={styles.title}>{children}</p>

export const OfferPerks = ({ children }: OfferChildProps): JSX.Element => (
  <>
    <strong className={styles.creditsGive}>{children} track downloads/month</strong>
    <p className={styles.featureGive}>+2 featured tracks/month</p>
  </>
)

export const OfferButton = ({
  children,
  offer,
  className,
  hasDiscount,
}: {
  children: string
  offer: string
  className?: string
  hasDiscount: boolean
}): JSX.Element => (
  <Button isFake className={clsx(styles.button, offer === 'Standard' && !hasDiscount && styles.preferred, className)}>
    {children}
  </Button>
)

type OfferProps = {
  tier: SubscriptionTier
  selected?: boolean
  period: TierPeriod
  perks?: Array<{ text: string }>
}

const Offer = ({ tier, selected, period, perks }: OfferProps): JSX.Element => {
  const { label, credits_give_per_month, description } = tier
  const profile = useProfile()
  const currency = profile?.id !== 0 ? profile.currency : CURRENCY
  const hasDiscount = period.price !== period.discount_offer_price
  const discountedPrice = priceFormatWithVAT(profile, period?.discount_offer_price, period?.discount_offer_price_inc_vat)

  const _description = hasDiscount ? 'LIMITED OFFER' : description

  return (
    <Link
      to={`/subscribe?period=${period.id}`}
      className={clsx(
        styles.offer,
        selected && styles.selected,
        label === 'Standard' && !hasDiscount && styles.offerPreferred,
        label === 'Standard' && hasDiscount && styles.discountStandardPreferred,
      )}
      datatesting={`subscription_offer_${tier.id}`}
    >
      <div>
        {description && (
          <OfferPill offer={label} hasDiscount={hasDiscount}>
            {_description}
          </OfferPill>
        )}
        {label && <OfferTitle>{label}</OfferTitle>}
        {period && !hasDiscount && (
          <>
            <p className={styles.price}>
              {period?.price && priceString(period.price, currency)}
              <span>/{monthsToText(period.months)}</span>
            </p>
            {profile.show_prices_with_vat && period?.price_inc_vat !== period?.price && (
              <p className={styles.vatPrice}>{`${priceString(period?.price_inc_vat, currency)} incl. VAT`}</p>
            )}
          </>
        )}
        {period && hasDiscount && (
          <>
            <p className={clsx(styles.price, styles.priceNew)}>
              {discountedPrice}
              <span>/{monthsToText(period.months)}</span>
            </p>
            <p className={clsx(styles.price, styles.priceOld)}>
              {priceFormatWithVAT(profile, period.price, period.price_inc_vat)}
              <span>/{monthsToText(period.months)}</span>
            </p>
          </>
        )}
        <OfferPerks>{credits_give_per_month}</OfferPerks>
        {perks && (
          <ul className={styles.perks}>
            {perks.map(({ text }) => (
              <li key={text}>
                <IconCheckmark />
                {text}
              </li>
            ))}
          </ul>
        )}
      </div>
      <OfferButton className={styles.buttonBlack} offer={label} hasDiscount={hasDiscount}>
        {selected ? 'Selected' : 'Select plan'}
      </OfferButton>
    </Link>
  )
}

export default Offer
